import { useCallback, useMemo } from "react";
import { FieldError, FieldErrorsImpl, Merge, useFormContext } from "react-hook-form";

import { Label, Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import { SelectOption } from "@components/AssessmentFormRenderer/types/layoutTypes";

interface FormTableSelectCellProps {
  fieldPath: string;
  options: string[] | SelectOption[];
  editable: boolean;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl>;
}

/** A single table-cell dropdown constrained to `options`, for `score_table`
 *  columns backed by an enum (e.g. BDI-3's MASTERY) or a per-row option set
 *  (e.g. BHI's per-subcategory score text via `optionsByRow`). Mirrors
 *  FormSingleSelect's value-binding pattern, generalized to an arbitrary
 *  field path. Plain strings are both label and value; SelectOption[] lets
 *  the displayed label differ from the stored value. */
export const FormTableSelectCell: React.FC<FormTableSelectCellProps> = ({ fieldPath, options, editable, error }) => {
  const { setValue, watch } = useFormContext();
  const watchValue = watch(fieldPath);

  const normalizedOptions: SelectOption[] = useMemo(
    () => options.map((opt) => (typeof opt === "string" ? { label: opt, value: opt } : opt)),
    [options],
  );

  const items: SelectItem[] = useMemo(
    () => normalizedOptions.map((opt, i) => ({ primary: opt.label, id: String(i) })),
    [normalizedOptions],
  );

  const selectedIndex = normalizedOptions.findIndex((opt) => opt.value === watchValue);
  const selectedItem = selectedIndex >= 0 ? items[selectedIndex] : null;
  const hasValue = watchValue != null && watchValue !== "";
  const isInvalidValue = hasValue && selectedIndex < 0;

  const handleSelect = useCallback(
    (item: SelectItem) => {
      const idx = item.id != null ? Number(item.id) : -1;
      const opt = normalizedOptions[idx];
      if (opt === undefined) return;
      setValue(fieldPath, opt.value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    },
    [fieldPath, normalizedOptions, setValue],
  );

  if (!editable) {
    return <Label>{hasValue ? String(watchValue) : "—"}</Label>;
  }

  let errorMessage: string | undefined;
  if (isInvalidValue) {
    errorMessage = `Not a recognized value: ${String(watchValue)}`;
  } else if (typeof error?.message === "string" && error.message) {
    // error.message isn't guaranteed to be a string -- Merge<FieldError,
    // FieldErrorsImpl> covers nested/array field errors too, where the
    // "message" slot can carry an object shape instead. String(object)
    // would silently render the literal text "[object Object]".
    errorMessage = error.message;
  }

  return (
    <Select
      title=""
      items={items}
      placeholderText="Select..."
      selected={selectedItem}
      setSelected={handleSelect}
      errorMessage={errorMessage}
      formNoValidate={!errorMessage}
      className="w-full"
      // Altered to allow the height to match other inputs
      inputClassName="web:text-sm"
      truncatePrimary={false}
    />
  );
};
